import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { configApi } from '../../api/config';
import { SUPPORTED_LANGUAGES, setLanguage } from '../../i18n';
import { openConsentSettings } from '../../lib/consent';
import { useHasAccount } from '../../state/authStore';
import { useThemeStore } from '../../state/themeStore';
import { BrandMark } from '../BrandMark';

/**
 * The marketing pages, in their Bulgarian form. Each is mirrored under /en, each ships a
 * pre-rendered head, and on each of them the URL — not the account or the browser — decides the
 * language. Everything else is the signed-in app, where the account's preference is what should win.
 */
const MARKETING_PATHS = ['/', '/pricing', '/calculator', '/privacy', '/terms', '/about', '/cookies'];

/**
 * Sections whose pages are not known in advance. An article exists because a database row says so,
 * so it cannot be listed above — but its URL names its language exactly as /pricing does, and for
 * the same reason: the server renders its head in that language and declares its hreflang pair.
 * Without this, a signed-in user whose account is set to English would open /blog/some-article and
 * read a Bulgarian article under English navigation.
 */
const MARKETING_PREFIXES = ['/blog'];

function isMarketingPath(bare: string): boolean {
  return (
    MARKETING_PATHS.includes(bare)
    || MARKETING_PREFIXES.some((prefix) => bare === prefix || bare.startsWith(`${prefix}/`))
  );
}

/** The language a marketing URL stands for: /en/* is English, everything else is Bulgarian. */
function languageForPath(pathname: string): 'bg' | 'en' {
  return pathname === '/en' || pathname.startsWith('/en/') ? 'en' : 'bg';
}

/**
 * The language this URL *insists* on, or null when the path names no language and the account's
 * preference should decide instead.
 *
 * Kept apart from {@link languageForPath}, which answers a narrower question — it is only ever asked
 * inside a marketing shell, where "not English" genuinely does mean Bulgarian. Asking it about
 * /dashboard would get a confident "bg" for a page that has no opinion.
 */
export function marketingLanguageForPath(pathname: string): 'bg' | 'en' | null {
  const bare = pathname === '/en' ? '/' : pathname.replace(/^\/en(?=\/)/, '');

  return isMarketingPath(bare) ? languageForPath(pathname) : null;
}

/** Marketing routes are mirrored under /en, so every internal link has to keep the visitor's side. */
export function useLandingPrefix() {
  const { pathname } = useLocation();
  return languageForPath(pathname) === 'en' ? '/en' : '';
}

/**
 * On marketing pages the URL decides the language, not the stored preference.
 *
 * Each of these routes ships a pre-rendered <head> in one language and declares an hreflang pair
 * pointing at its twin. If a Bulgarian visitor's stored preference could make /pricing render in
 * English, that markup would be describing a page that does not exist, and the visitor would be
 * reading the wrong language on a URL that promises the other one.
 */
function useLanguageFromPath() {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const wanted = languageForPath(pathname);

  useEffect(() => {
    if (i18n.resolvedLanguage !== wanted) {
      void setLanguage(wanted);
    }
  }, [i18n, wanted]);
}

/**
 * Switching language on a marketing page is a navigation, not a state change: the mirrored URL is
 * the canonical home of the other language, and staying put would leave the address bar and the
 * <head> describing the language the visitor just left.
 */
function useLanguageSwitcher() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const alternatePath = useContext(AlternatePathContext);

  return (next: string) => {
    // A page that knows where its other-language version lives says so. Mirroring the URL works
    // for /pricing ↔ /en/pricing, but an article's two versions have different slugs, and
    // /en/blog/<a Bulgarian slug> is a 404.
    if (alternatePath !== undefined) {
      void navigate(alternatePath);
      return;
    }

    const bare = pathname === '/en' ? '/' : pathname.replace(/^\/en(?=\/)/, '');
    void navigate(next === 'en' ? (bare === '/' ? '/en' : `/en${bare}`) : bare);
  };
}

/**
 * Where the language switcher should go from this page, when mirroring the URL would be wrong.
 * Undefined — the default — means "mirror it".
 */
const AlternatePathContext = createContext<string | undefined>(undefined);

/**
 * The nav sits inside the dark hero panel rather than above it, so it is styled for a dark
 * background and passed as a child of that panel by each page.
 */
export function LandingNav() {
  const { t, i18n } = useTranslation();
  const prefix = useLandingPrefix();
  const switchLanguage = useLanguageSwitcher();
  const toggleTheme = useThemeStore((s) => s.toggle);
  const hasAccount = useHasAccount();

  return (
    <header className="l-header">
      <nav className="l-nav">
        <Link className="l-brand" to={prefix || '/'}>
          <BrandMark size={27} />
          {t('app.name')}
        </Link>

        <div className="l-nav-links">
          <Link to={`${prefix}/calculator`}>{t('marketing.nav.calculator')}</Link>
          <a href={`${prefix}/#features`}>{t('marketing.nav.features')}</a>
          <a href={`${prefix}/#how`}>{t('marketing.nav.how')}</a>
          <Link to={`${prefix}/pricing`}>{t('marketing.nav.pricing')}</Link>
          <Link to={`${prefix}/blog`}>{t('blog.nav')}</Link>
        </div>

        <div className="l-nav-cta">
          <select
            className="l-select"
            aria-label={t('language.label')}
            value={i18n.resolvedLanguage}
            onChange={(e) => switchLanguage(e.target.value)}
          >
            {SUPPORTED_LANGUAGES.map((lng) => (
              <option key={lng} value={lng}>
                {t(`language.${lng}`)}
              </option>
            ))}
          </select>
          <button type="button" className="l-select" onClick={toggleTheme} aria-label="Theme">
            ☾ / ☀
          </button>
          {/* Somebody signed in is shown the way back to their account, not an invitation to open
              a second one — see useHasAccount. A stranger gets both doors: until now the only way
              to sign in from here was a link in the footer, so a returning visitor whose session
              had lapsed was offered sign-up and nothing else. */}
          {hasAccount ? (
            <Link className="l-btn" to="/dashboard">
              {t('marketing.nav.toApp')}
            </Link>
          ) : (
            <>
              <Link className="l-nav-signin" to="/login">
                {t('auth.signIn')}
              </Link>
              <Link className="l-btn" to="/register">
                {t('home.cta')}
              </Link>
            </>
          )}
        </div>
      </nav>
    </header>
  );
}

export function LandingPromo() {
  const { t } = useTranslation();
  return <div className="l-promo">{t('marketing.promo')}</div>;
}

function LandingFooter() {
  const { t } = useTranslation();
  const prefix = useLandingPrefix();
  // For the two footer entries that exist only when configured: cookie settings and a contact address.
  const { data: config } = useQuery({ queryKey: ['config'], queryFn: configApi.get });
  const hasAccount = useHasAccount();

  return (
    <footer className="l-footer">
      <div className="l-footer-grid">
        <div>
          <span className="l-brand" style={{ color: 'var(--l-ink)' }}>
            <BrandMark size={27} />
            {t('app.name')}
          </span>
          <p className="l-footer-about">{t('marketing.footer.about')}</p>
        </div>

        <div>
          <h4>{t('marketing.footer.product')}</h4>
          <Link to={`${prefix}/calculator`}>{t('marketing.nav.calculator')}</Link>
          <Link to={`${prefix}/pricing`}>{t('marketing.nav.pricing')}</Link>
          <Link to={`${prefix}/blog`}>{t('blog.nav')}</Link>
          <Link to={`${prefix}/about`}>{t('about.nav')}</Link>
          {hasAccount ? (
            <Link to="/dashboard">{t('marketing.nav.toApp')}</Link>
          ) : (
            <>
              <Link to="/register">{t('home.cta')}</Link>
              <Link to="/login">{t('auth.signIn')}</Link>
            </>
          )}
        </div>

        <div>
          <h4>{t('marketing.footer.legal')}</h4>
          {/* Чл. 4 ЗЕТ wants the provider's identity permanently and directly accessible, and the
              terms are where it lives — so the link belongs in the footer of every page, not only
              on the pages that ask someone to accept it. */}
          <Link to={`${prefix}/terms`}>{t('terms.title')}</Link>
          <Link to={`${prefix}/privacy`}>{t('privacy.title')}</Link>
          <Link to={`${prefix}/cookies`}>{t('cookies.title')}</Link>
          {/* Taking consent back has to be as easy as giving it, from any page — so it lives here
              and not three clicks into a policy. Only when there is something to have consented to. */}
          {config?.tracking && (
            <button type="button" className="l-footer-link-button" onClick={openConsentSettings}>
              {t('consent.settings')}
            </button>
          )}
          {config?.supportEmail && (
            <>
              <h4 className="l-footer-subhead">{t('marketing.footer.contact')}</h4>
              <a href={`mailto:${config.supportEmail}`}>{config.supportEmail}</a>
            </>
          )}
        </div>

        <div>
          <h4>{t('language.label')}</h4>
          <Link to="/">Български</Link>
          <Link to="/en">English</Link>
        </div>
      </div>

      <div className="l-footer-bottom">
        <p>{t('app.disclaimer')}</p>
        <p>© {new Date().getFullYear()} Danak</p>
      </div>
    </footer>
  );
}

interface LandingShellProps {
  children: ReactNode;
  /**
   * The same page in the other language, for pages whose twin is not simply this URL with /en in
   * front of it. Leave it out everywhere else.
   */
  alternatePath?: string;
}

export function LandingShell({ children, alternatePath }: LandingShellProps) {
  useLanguageFromPath();

  return (
    <AlternatePathContext.Provider value={alternatePath}>
      <div className="landing">
        {children}
        <LandingFooter />
      </div>
    </AlternatePathContext.Provider>
  );
}

/** Dark panel that carries the promo bar and nav, used at the top of every marketing page. */
export function LandingTop({ children }: { children: ReactNode }) {
  return (
    <div className="l-top">
      <LandingPromo />
      <LandingNav />
      {children}
    </div>
  );
}
